// Regeln „Wenn … dann …“ (Phase 2.C): Bedingungen und Aktionen als Zod-Schemas, gemeinsam für
// Worker (Ausführung), API (Prüfung, Probelauf) und Web (Editor, Anzeige).
import { z } from 'zod';

export const BedingungSchema = z.discriminatedUnion('art', [
  z.object({ art: z.literal('absender_adresse'), wert: z.string().min(1) }), // enthält (z. B. jana.kurz@…)
  z.object({ art: z.literal('absender_domain'), wert: z.string().min(1) }), // z. B. gs-lindenpark.de
  z.object({ art: z.literal('absender_staff') }), // gehört zur CRM-Tabelle staff
  z.object({ art: z.literal('absender_crm') }), // ist Kontakt einer Einrichtung im CRM
  z.object({ art: z.literal('absender_unbekannt') }),
  z.object({ art: z.literal('postfach'), wert: z.string().min(1) }), // Adresse (enthält)
  z.object({ art: z.literal('betreff_enthaelt'), wert: z.string().min(1) }),
  z.object({ art: z.literal('text_enthaelt'), wert: z.string().min(1) }),
  z.object({ art: z.literal('anhang'), typ: z.enum(['vorhanden', 'pdf']).default('vorhanden') }),
  z.object({ art: z.literal('wochentag'), tage: z.array(z.number().int().min(0).max(6)).min(1) }), // 0 = Sonntag
  z.object({ art: z.literal('uhrzeit'), von: z.string().regex(/^\d{2}:\d{2}$/), bis: z.string().regex(/^\d{2}:\d{2}$/) }),
  z.object({ art: z.literal('bob_erkennt'), text: z.string().min(3), minSicherheit: z.number().min(0).max(1).default(0.8) }),
]);
export type Bedingung = z.infer<typeof BedingungSchema>;

export const BedingungenSchema = z.object({
  alle: z.array(BedingungSchema).default([]), // und
  eine: z.array(BedingungSchema).default([]), // oder-Gruppe (mindestens eine), optional
});
export type Bedingungen = z.infer<typeof BedingungenSchema>;

export const AktionSchema = z.discriminatedUnion('typ', [
  z.object({ typ: z.literal('easybill'), bestaetigung: z.boolean().default(true) }), // PDF hochladen, Bestätigung an Absender, erledigt
  z.object({ typ: z.literal('weiterleiten'), an: z.string().email(), modus: z.enum(['weiterleitung', 'umleitung']).default('weiterleitung') }),
  z.object({ typ: z.literal('antworten'), betreff: z.string().max(200).optional(), text: z.string().min(1).max(5000) }),
  z.object({ typ: z.literal('zuweisen'), an: z.enum(['till', 'lars', 'maren']) }),
  z.object({ typ: z.literal('erledigt') }),
  z.object({ typ: z.literal('einsortieren'), kategorie: z.enum(['info', 'junk']) }),
  z.object({ typ: z.literal('spaeter'), preset: z.enum(['today16', 'tomorrow', 'monday', 'nextweek']).default('tomorrow') }),
  z.object({ typ: z.literal('crm_notiz'), text: z.string().min(1).max(500) }),
  z.object({ typ: z.literal('crm_erinnerung'), titel: z.string().min(1).max(200), tage: z.number().int().min(0).max(365).default(1), fuer: z.enum(['till', 'lars', 'maren']).optional() }),
  z.object({ typ: z.literal('bob_entwurf'), hinweis: z.string().max(500).optional() }),
]);
export type Aktion = z.infer<typeof AktionSchema>;

export const RegelSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1).max(120),
  aktiv: z.boolean().default(false),
  reihenfolge: z.number().int().default(100),
  bedingungen: BedingungenSchema,
  aktionen: z.array(AktionSchema).min(1),
  beschreibung: z.string().max(2000).nullable().optional(),
});
export type Regel = z.infer<typeof RegelSchema>;

// Nur Admins: Weiterleitung und easybill-Übergabe
export const ADMIN_AKTIONEN = new Set<Aktion['typ']>(['easybill', 'weiterleiten']);

export const BEDINGUNG_LABEL: Record<Bedingung['art'], string> = {
  absender_adresse: 'Absender-Adresse enthält',
  absender_domain: 'Absender-Domain ist',
  absender_staff: 'Absender gehört zum Staff (CRM)',
  absender_crm: 'Absender ist CRM-Kontakt einer Einrichtung',
  absender_unbekannt: 'Absender ist unbekannt',
  postfach: 'Postfach enthält',
  betreff_enthaelt: 'Betreff enthält',
  text_enthaelt: 'Text enthält',
  anhang: 'Anhang',
  wochentag: 'Wochentag',
  uhrzeit: 'Uhrzeit zwischen',
  bob_erkennt: 'Bob erkennt …',
};
export const AKTION_LABEL: Record<Aktion['typ'], string> = {
  easybill: 'An easybill übergeben',
  weiterleiten: 'Weiterleiten',
  antworten: 'Antworten mit Vorlage',
  zuweisen: 'Zuweisen',
  erledigt: 'Als erledigt markieren',
  einsortieren: 'Einsortieren (Info/Junk)',
  spaeter: 'Später vorlegen',
  crm_notiz: 'CRM-Notiz an der Einrichtung',
  crm_erinnerung: 'CRM-Erinnerung setzen',
  bob_entwurf: 'Bob-Entwurf vorbereiten',
};

export function bedingungText(b: Bedingung): string {
  switch (b.art) {
    case 'absender_adresse':
    case 'absender_domain':
    case 'postfach':
    case 'betreff_enthaelt':
    case 'text_enthaelt':
      return `${BEDINGUNG_LABEL[b.art]} „${b.wert}“`;
    case 'anhang':
      return b.typ === 'pdf' ? 'Anhang ist eine PDF' : 'Anhang vorhanden';
    case 'wochentag':
      return 'Wochentag ' + b.tage.map((t) => ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][t]).join(', ');
    case 'uhrzeit':
      return `Uhrzeit zwischen ${b.von} und ${b.bis}`;
    case 'bob_erkennt':
      return `Bob erkennt: „${b.text}“ (ab ${Math.round(b.minSicherheit * 100)} % Sicherheit)`;
    default:
      return BEDINGUNG_LABEL[b.art];
  }
}
export function aktionText(a: Aktion): string {
  switch (a.typ) {
    case 'weiterleiten':
      return `${a.modus === 'umleitung' ? 'Umleiten' : 'Weiterleiten'} an ${a.an}`;
    case 'antworten':
      return `Antworten mit Vorlage${a.betreff ? ` „${a.betreff}“` : ''}`;
    case 'zuweisen':
      return `Zuweisen an ${a.an}`;
    case 'einsortieren':
      return `Einsortieren als ${a.kategorie}`;
    case 'spaeter':
      return `Später vorlegen (${{ today16: 'heute 16:00', tomorrow: 'morgen früh', monday: 'Montag', nextweek: 'in einer Woche' }[a.preset]})`;
    case 'crm_notiz':
      return `CRM-Notiz: „${a.text.slice(0, 60)}“`;
    case 'crm_erinnerung':
      return `CRM-Erinnerung „${a.titel}“ in ${a.tage} Tag(en)${a.fuer ? ` für ${a.fuer}` : ''}`;
    case 'easybill':
      return 'An easybill übergeben' + (a.bestaetigung ? ' und Empfang bestätigen' : '');
    case 'bob_entwurf':
      return 'Bob-Entwurf vorbereiten' + (a.hinweis ? ` (${a.hinweis})` : '');
    default:
      return AKTION_LABEL[a.typ];
  }
}
